import { Fragment, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ShrinkWrapPanel from "@/components/ui/ShrinkWrapPanel";
import { usePreloadImages } from "@/lib/usePreloadImages";

const LEGACY_DEFAULTS = {
  eyebrow: "EXQURA GOLF & EXPERIENCES",
  headline: "Feel the\nFairway",
  subtext: "Exclusive tee times — book in seconds",
  backgroundImage: "/home.jpeg",
};

const AUTO_PLAY_MS = 6000;

function HeadlineText({ headline }) {
  return headline.split("\n").map((line, i, arr) => (
    <Fragment key={i}>
      {line}
      {i < arr.length - 1 && <br />}
    </Fragment>
  ));
}

export default function ExquraHero({ fields = {} }) {
  // Back-compat: older published content stored the hero as a single flat
  // object (eyebrow/headline/subtext/backgroundImage) with no `slides`
  // array. Fall back to wrapping that as a one-slide list so nothing
  // regresses until an admin actually adds more slides in "Edit Site".
  const slides =
    Array.isArray(fields.slides) && fields.slides.length > 0
      ? fields.slides
      : [
          {
            eyebrow: fields.eyebrow ?? LEGACY_DEFAULTS.eyebrow,
            headline: fields.headline ?? LEGACY_DEFAULTS.headline,
            subtext: fields.subtext ?? LEGACY_DEFAULTS.subtext,
            backgroundImage:
              fields.backgroundImage ?? LEGACY_DEFAULTS.backgroundImage,
          },
        ];

  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const touchStartX = useRef(null);
  const count = slides.length;
  const showControls = count > 1;

  const goTo = (idx) => setCurrent((idx + count) % count);
  const prev = () => goTo(current - 1);
  const next = () => goTo(current + 1);

  useEffect(() => {
    if (!showControls || paused) return;
    timerRef.current = setTimeout(next, AUTO_PLAY_MS);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, paused, showControls]);

  usePreloadImages(slides.map((s) => s.backgroundImage));

  const slide = slides[current];

  return (
    <section
      className="relative min-h-[580px] flex items-end overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => (touchStartX.current = e.touches[0].clientX)}
      onTouchEnd={(e) => {
        // Arrows are hidden on mobile, so swipe left/right to change slides
        if (count < 2 || touchStartX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(dx) > 40) (dx < 0 ? next : prev)();
      }}
    >
      {/* Background image, cross-fades between slides */}
      <AnimatePresence mode="sync">
        <motion.div
          key={current}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7 }}
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${slide.backgroundImage}')` }}
        />
      </AnimatePresence>

      {/* Text Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`text-${current}`}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.4 }}
          className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-15 pb-16"
        >
          {/* Frosted dark panel keeps copy legible on bright photos */}
          <ShrinkWrapPanel className="w-fit max-w-2xl border border-white/10 bg-black/40 px-6 py-6 sm:px-8 sm:py-7 shadow-[0_8px_32px_rgba(0,0,0,0.25)] backdrop-blur-md">
            <p className="text-[#7ccfbd] text-sm sm:text-base font-semibold tracking-[0.1m] uppercase mb-4">
              {slide.eyebrow}
            </p>
            <h1 className="text-white font-bold text-5xl sm:text-6xl lg:text-7xl tracking-[0.1m] leading-tight mb-3">
              <HeadlineText headline={slide.headline} />
            </h1>
            <p className="text-white/85 text-base sm:text-lg">{slide.subtext}</p>
          </ShrinkWrapPanel>
        </motion.div>
      </AnimatePresence>

      {showControls && (
        <>
          {/* Prev / Next arrows */}
          <button
            type="button"
            onClick={prev}
            aria-label="Previous slide"
            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 hidden lg:flex items-center justify-center text-white backdrop-blur-sm transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next slide"
            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 hidden lg:flex items-center justify-center text-white backdrop-blur-sm transition-colors"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          {/* Dot indicators */}
          <div className="absolute bottom-6 right-4 sm:right-8 z-20 flex gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`rounded-full transition-all duration-300 ${
                  i === current ? "w-6 h-2 bg-white" : "w-2 h-2 bg-white/50 hover:bg-white/80"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
